import { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Modal,
  IconButton,
  Paper
} from '@mui/material';
import { Close } from '@mui/icons-material';
import posthog from '../../../utils/posthog';

interface CatalogueGridProps {
  header: string;
  catalogueData: { id: string; img: string; url: string }[];
}

interface CatalogueItem {
  id: string;
  img: string;
  url: string;
}

const CatalogueGrid = ({ header, catalogueData }: CatalogueGridProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCatalogue, setSelectedCatalogue] = useState<CatalogueItem | null>(null);

  const handleCatalogueClick = (catalogue: CatalogueItem) => {
    setSelectedCatalogue(catalogue);
    setModalOpen(true);

    posthog.capture('catalogue_opened', {
      catalogue_id: catalogue.id,
      catalogue_url: catalogue.url,
      source: 'consumer',
    });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCatalogue(null);
  };

  return (
    <>
      <Box
        sx={{
          background: '#EFEFEF',
          py: { xs: '20px', lg: '103px' },
          px: '40px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: '500',
            color: '#000',
            fontSize: '56px',
            lineHeight: '32px',
            mb: 8,
          }}
          variant='h1'
        >
          {header}
        </Typography>

        <Grid container spacing={8}>
          {catalogueData
            .filter(elem => elem.id !== '001')
            .map((elem, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                  onClick={() => handleCatalogueClick(elem)}
                >
                  <img
                    src={elem.img}
                    alt={`Catalogue ${index + 1}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }}
                  />
                </Box>
              </Grid>
            ))}
        </Grid>
      </Box>

      {/* Catalogue Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="catalogue-modal"
        aria-describedby="catalogue-viewer"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper
          sx={{
            position: 'relative',
            width: '95vw',
            height: '90vh',
            maxWidth: '1200px',
            maxHeight: '800px',
            outline: 'none',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 24,
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
              },
            }}
          >
            <Close />
          </IconButton>

          {/* Iframe Container */}
          {selectedCatalogue && (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                paddingTop: 'max(60%, 326px)', // Responsive aspect ratio
              }}
            >
              <iframe
                allow="clipboard-write"
                sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-scripts allow-same-origin allow-popups allow-modals allow-popups-to-escape-sandbox allow-forms"
                allowFullScreen={true}
                style={{
                  position: 'absolute',
                  border: 'none',
                  width: '100%',
                  height: '100%',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                }}
                src={selectedCatalogue.url}
                title={`Catalogue ${selectedCatalogue.id}`}
              />
            </Box>
          )}
        </Paper>
      </Modal>
    </>
  );
};

export default CatalogueGrid;